'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  SupportTicket, TicketStatus,
  getAllTickets, updateTicketStatus, addMessageToTicket, getTicketStats, seedDemoTickets,
  CATEGORY_LABELS, STATUS_LABELS, PRIORITY_LABELS,
} from '@/lib/supportTickets';
import { sendWhatsAppNotification } from '@/lib/smsNotification';

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  waiting_customer: 'bg-purple-100 text-purple-700 border-purple-200',
  resolved: 'bg-green-100 text-green-700 border-green-200',
  closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600', medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700',
};

export default function SupportTicketAdmin() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, urgent: 0, high: 0, waitingCustomer: 0, closed: 0 });
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [filterStatus, setFilterStatus] = useState<TicketStatus | 'all'>('all');
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const agentName = 'Admin Agent';

  useEffect(() => { seedDemoTickets(); refreshData(); }, []);

  function refreshData() {
    const all = getAllTickets();
    setTickets(all);
    setStats(getTicketStats());
    if (selectedTicket) {
      const updated = all.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
    }
  }

  const filtered = filterStatus === 'all' ? tickets : tickets.filter(t => t.status === filterStatus);

  function handleStatusChange(ticketId: string, status: TicketStatus) {
    updateTicketStatus(ticketId, status, agentName);
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket && status === 'resolved') {
      sendWhatsAppNotification({ type: 'support_ticket_updated', recipientName: ticket.customerName, recipientPhone: ticket.customerPhone, ticketId: ticket.ticketNumber, ticketStatus: 'Resolved', agentName, message: 'Your support ticket has been resolved.' });
    }
    refreshData();
  }

  function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTicket || !replyText.trim()) return;
    setIsSending(true);
    setTimeout(() => {
      const updated = addMessageToTicket(selectedTicket.id, replyText, agentName, 'agent');
      if (updated) {
        sendWhatsAppNotification({ type: 'support_ticket_updated', recipientName: selectedTicket.customerName, recipientPhone: selectedTicket.customerPhone, ticketId: selectedTicket.ticketNumber, ticketStatus: 'Agent replied', agentName, message: replyText });
      }
      setReplyText('');
      setIsSending(false);
      refreshData();
    }, 400);
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-foreground', bg: 'bg-surface' },
          { label: 'Open', value: stats.open, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'In Progress', value: stats.inProgress, color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Urgent', value: stats.urgent, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center border border-border`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', 'open', 'in_progress', 'waiting_customer', 'resolved', 'closed'] as const).map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
            filterStatus === s ? 'bg-primary text-white border-primary' : 'bg-white text-muted-foreground border-border hover:border-primary/40'
          }`}>
            {s === 'all' ? 'All' : STATUS_LABELS[s as TicketStatus]}
            {s !== 'all' && <span className="ml-1 opacity-70">({tickets.filter(t => t.status === s).length})</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon name="InboxIcon" size={40} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No tickets in this category</p>
            </div>
          ) : (
            filtered.map(ticket => (
              <div key={ticket.id} onClick={() => setSelectedTicket(ticket)} className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                selectedTicket?.id === ticket.id ? 'border-primary ring-2 ring-primary/20' : 'border-border'
              }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs text-muted-foreground">{ticket.ticketNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold border ${STATUS_COLORS[ticket.status]}`}>{STATUS_LABELS[ticket.status]}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${PRIORITY_COLORS[ticket.priority]}`}>{PRIORITY_LABELS[ticket.priority]}</span>
                    </div>
                    <p className="font-bold text-sm text-foreground truncate">{ticket.subject}</p>
                    <p className="text-xs text-muted-foreground">{ticket.customerName} · {ticket.customerEmail}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{new Date(ticket.createdAt).toLocaleDateString('en-GB')}</p>
                    <p className="text-xs text-muted-foreground">{ticket.messages.length} msgs</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{CATEGORY_LABELS[ticket.category]}</p>
              </div>
            ))
          )}
        </div>

        <div className="bg-white rounded-xl border border-border p-5">
          {!selectedTicket ? (
            <div className="text-center py-16 text-muted-foreground">
              <Icon name="ChatBubbleLeftRightIcon" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium">Select a ticket to view details</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-sm text-muted-foreground">{selectedTicket.ticketNumber}</p>
                  <h3 className="font-bold text-foreground">{selectedTicket.subject}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTicket.customerName} · {selectedTicket.customerEmail}</p>
                </div>
                <select value={selectedTicket.status} onChange={e => handleStatusChange(selectedTicket.id, e.target.value as TicketStatus)} className={`text-xs px-3 py-1.5 rounded-lg font-bold border cursor-pointer ${STATUS_COLORS[selectedTicket.status]}`}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-surface rounded-lg p-2"><p className="text-muted-foreground">Category</p><p className="font-bold">{CATEGORY_LABELS[selectedTicket.category]}</p></div>
                <div className="bg-surface rounded-lg p-2"><p className="text-muted-foreground">Priority</p><p className="font-bold">{PRIORITY_LABELS[selectedTicket.priority]}</p></div>
                {selectedTicket.bookingRef && <div className="bg-surface rounded-lg p-2"><p className="text-muted-foreground">Booking Ref</p><p className="font-mono font-bold">{selectedTicket.bookingRef}</p></div>}
                {selectedTicket.customerPhone && <div className="bg-surface rounded-lg p-2"><p className="text-muted-foreground">Phone</p><p className="font-bold">{selectedTicket.customerPhone}</p></div>}
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-xl p-3">
                {selectedTicket.messages.map(msg => (
                  <div key={msg.id} className={`rounded-lg p-2.5 text-xs ${
                    msg.authorRole === 'customer' ? 'bg-blue-50 border border-blue-100'
                    : msg.authorRole === 'agent'? 'bg-green-50 border border-green-100' :'bg-gray-50 text-center text-muted-foreground'
                  }`}>
                    {msg.authorRole !== 'system' && <p className="font-bold mb-0.5">{msg.authorName} · {new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>}
                    <p>{msg.content}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleReply} className="space-y-2">
                <textarea rows={3} value={replyText} onChange={e => setReplyText(e.target.value)} className="w-full border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Type your response to the customer..." />
                <div className="flex gap-2">
                  <button type="submit" disabled={isSending || !replyText.trim()} className="flex-1 bg-primary text-white py-2.5 rounded-xl font-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {isSending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Icon name="PaperAirplaneIcon" size={16} />}
                    Send Reply + WhatsApp
                  </button>
                  {selectedTicket.customerPhone && (
                    <a href={`https://wa.me/${selectedTicket.customerPhone.replace(/\D/g, '').replace(/^0/, '44')}`} target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition-colors">
                      <Icon name="ChatBubbleLeftIcon" size={16} />
                    </a>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
